import { LoaderTransparent } from 'components/molecules/LoaderTransparent/LoaderTransparent'
import { useEffect, useState } from 'react'
import { useSelector } from 'react-redux'
import { getTemplateDetail } from 'services/templateService'
import { RootState } from 'store/store'
import HeaderPageSection from 'core/presentation/components/molecules/HeaderPageSection/HeaderPageSection'
import HeaderPatternDetail from 'components/molecules/HeaderPatternDetail/HeaderPatternDetail'
import AppBox from 'components/atoms/AppBox/AppBox'
import { AccordionPattern } from 'components/molecules/AccordionPattern/AccordionPattern'
import moment from 'moment'
import { Criteria } from 'interfaces/patternDetails'
import ImageEmptyList from 'components/molecules/ImageEmptyList/ImageEmptyList'
import EmptyBox from '../../../assets/empty-box.png'
import { useNavigate } from 'react-router-dom'
import Layout from 'layout/Layout'
import AppButton from 'components/atoms/AppButton/AppButton'
import { usePermissions } from 'hooks/usePermissions'
import { RolePermissions } from 'utils/constants'
import { OperationMode } from 'core/domain/operationMode'
import { OperationModeBadge } from 'core/presentation/components/molecules/OperationModeBadge/OperationModeBadge'

interface Props {
    pattern: number
}

export default function PatternRulesList({ pattern }: Props) {
    const navigate = useNavigate()
    const { apiToken } = useSelector((state: RootState) => state.auth)
    const [loading, setLoading] = useState(false)
    const { can } = usePermissions()

    interface RuleGroup {
        id: number
        name: string
        chutes: number[]
        priority?: number | null
        criterias: Criteria[]
        providerService?: {
            code: string
            name: string
        } | null
        change_route?: boolean
    }

    interface RulesList {
        id: number | null
        createdAt: string
        updatedAt: string
        chutesAssigned: number[]
        chutesNotAssigned: number[]
        name: string
        groups: RuleGroup[]
        operationMode?: OperationMode
    }

    const [rulesList, setRulesList] = useState<RulesList>({
        id: null,
        createdAt: '',
        updatedAt: '',
        chutesAssigned: [],
        chutesNotAssigned: [],
        name: '',
        groups: [],
        operationMode: undefined,
    })

    const isClassification = rulesList.operationMode?.code === 'classification'

    const breadcrumbNavigate = [
        {
            label: 'Plantillas',
            path: '/templates',
        },
        {
            label: `${rulesList.name}`,
            path: '/packages/create',
        },
    ]

    useEffect(() => {
        const fetchData = async () => {
            try {
                setLoading(true)
                const data = await getTemplateDetail(pattern)
                if (data !== undefined && data !== null) {
                    const sortedGroups = (data.groups ?? [])
                        .slice()
                        .sort((a: RuleGroup, b: RuleGroup) => (a.priority ?? 0) - (b.priority ?? 0))
                    setRulesList({
                        id: data.id,
                        name: data.name,
                        createdAt: data.created_at,
                        updatedAt: data.updated_at,
                        chutesAssigned: data.chutes_assigned ?? [],
                        chutesNotAssigned: data.chutes_not_assigned ?? [],
                        groups: sortedGroups,
                        operationMode: data.classificationType
                            ? {
                                  id: 0,
                                  code: data.classificationType.key,
                                  name: data.classificationType.description,
                              }
                            : undefined,
                    })
                }
                setLoading(false)
            } catch (error) {
                setLoading(false)
            }
        }

        fetchData()
    }, [apiToken])

    return (
        <Layout>
            <HeaderPageSection
                title="Detalle de la plantilla"
                subtitle={
                    rulesList.updatedAt
                        ? `Última actualización ${moment(rulesList.updatedAt).format('DD/MM/YYYY H:mm')}`
                        : undefined
                }
                breadcrumbItems={breadcrumbNavigate}
                badgeNode={
                    rulesList.operationMode && (
                        <OperationModeBadge
                            operationMode={rulesList.operationMode}
                        />
                    )
                }
            >
                {can(RolePermissions.templates.edit) && (
                    <AppButton
                        title="Agregar reglas"
                        state="primary"
                        className="text-sm font-bold"
                        onClick={() => navigate(`/templates/edit/${pattern}`)}
                    />
                )}
            </HeaderPageSection>
            <>
                {loading && <LoaderTransparent />}
                <HeaderPatternDetail
                            created={{
                                createdAt: moment(rulesList.createdAt).format('DD/MM/YYYY'),
                                hour: moment(rulesList.createdAt).format('HH:mm'),
                            }}
                            countRules={rulesList.groups.length}
                            chutesAssigned={
                                Array.isArray(rulesList.chutesAssigned)
                                    ? {
                                        totals: rulesList.chutesAssigned.length,
                                        notProcessed: Array.isArray(rulesList.chutesNotAssigned)
                                            ? rulesList.chutesNotAssigned.length
                                            : 0,
                                    }
                                    : { totals: 0, notProcessed: 0 }
                            }
                            isClassification={isClassification}
                        />
                        <AppBox>
                            <div className="bg-[#F1F5F9] px-[12px] py-2 flex justify-between items-center rounded-xl">
                                <p className="text-sm font-normal text-[#141A1F] w-2/3">
                                    Nombre de regla
                                </p>
                                {!isClassification && (
                                    <p className="text-sm font-normal text-[#141A1F] w-2/3 text-end">
                                        Chutes asignados
                                    </p>
                                )}
                            </div>
                            <div
                                className="overflow-y-auto"
                                style={{ height: '500px' }}
                            >
                                {rulesList.groups.length > 0 ? (
                                    <div className="flex justify-center py-3 text-sm">
                                        <AccordionPattern
                                            rules={rulesList.groups}
                                            isClassification={isClassification}
                                        />
                                    </div>
                                ) : (
                                    <div className="flex justify-center p-4 text-sm">
                                        <ImageEmptyList
                                            image={EmptyBox}
                                            title="Aún no has agregado ninguna regla"
                                            description="Asigna una o más reglas para esta plantilla"
                                            textButton="Agregar reglas"
                                            onClick={() =>
                                                navigate(
                                                    `/templates/edit/${pattern}`
                                                )
                                            }
                                        />
                                    </div>
                                )}
                            </div>
                        </AppBox>
            </>
        </Layout>
    )
}
